/**
 * Dual-mode source of the caregiver's linked children + engagement data (P1).
 *
 * Fixtures mode (vitest / no Supabase env): the original synchronous fixture
 * hub, unchanged — every existing test keeps its data.
 * Live mode: builds the SAME LinkedChild[] shape from getPortalData() joins
 * (children ∪ childLinks ∪ consents ∪ sessionSummaries ∪ skillSummaries ∪
 * accounts) keyed on the REAL signed-in account id — the fix for the portal
 * showing "acc-lena"'s fixtures to every live user.
 *
 * OPAQUE REFS: server children carry zero PII, so live display labels are
 * synthesized client-side ("Child 1", "Child 2", … in stable id order).
 */
import { useCallback, useEffect, useState } from "react";
import { getPortalData, shouldUseSupabase } from "../../data";
import type { Child, ChildLink, Consent, HomeNote } from "../../domain/types";
import {
  FIXTURE_ACCOUNT_ID,
  getLinkedChildren,
  newestFirst,
  type LinkedChild,
  type LinkState,
} from "./data";

function resolveLinkState(link: ChildLink, consent: Consent | undefined): LinkState {
  if (link.relation === "owner") return "active";
  if (!consent) return "pending";
  if (consent.revokedAt) return "revoked";
  return "active";
}

async function fetchLinkedChildren(accountId: string): Promise<LinkedChild[]> {
  const ds = getPortalData();
  const [children, links, consents, sessions, skills, accounts, stepStates, supportProfiles, assignments, homeNotes] =
    await Promise.all([
      ds.children(),
      ds.childLinks(),
      ds.consents(),
      ds.sessionSummaries(),
      ds.skillSummaries(),
      ds.accounts().catch(() => []),
      // The progress-views tables arrive with migration 20261022000007. A
      // backend that does not have them yet (or a read RLS refuses) must not
      // take the whole page down: the screens fall back to recent play.
      ds.stepStates().catch(() => []),
      ds.supportProfiles().catch(() => []),
      ds.assignments().catch(() => []),
      // home_note arrives with migration 20261022000008; same fallback.
      Promise.resolve().then(() => ds.homeNotes()).catch(() => [] as HomeNote[]),
    ]);
  const labelOrder = [...children].sort((a, b) => a.id.localeCompare(b.id));
  const labelFor = (id: string) => `Child ${labelOrder.findIndex((c) => c.id === id) + 1}`;

  return links
    .filter((l) => l.accountId === accountId)
    .flatMap((link) => {
      const server = children.find((c) => c.id === link.childId);
      if (!server) return [];
      const child: Child = {
        id: server.id,
        ownerAccountId: server.ownerAccountId,
        displayLabel: labelFor(server.id),
      };
      const consent = link.consentId ? consents.find((c) => c.id === link.consentId) : undefined;
      const linkState = resolveLinkState(link, consent);
      const visible = linkState === "active";
      const owner = accounts.find((a) => a.id === server.ownerAccountId);
      return [
        {
          child,
          link,
          consent,
          linkState,
          ownerEmail: link.relation === "owner" ? undefined : owner?.email,
          // selectAll is unordered: sort so "recent play" and the sync date are true.
          sessions: visible ? newestFirst(sessions.filter((s) => s.childId === link.childId)) : [],
          skills: visible ? skills.filter((s) => s.childId === link.childId) : [],
          stepStates: visible ? stepStates.filter((s) => s.childId === link.childId) : [],
          supportProfile: visible
            ? supportProfiles.find((p) => p.childId === link.childId) ?? null
            : null,
          assignments: visible ? newestFirst(assignments.filter((a) => a.childId === link.childId)) : [],
          homeNotes: visible ? homeNotes.filter((n) => n.childId === link.childId) : [],
        },
      ];
    });
}

export interface LinkedChildrenState {
  linked: LinkedChild[];
  loading: boolean;
  /**
   * Non-empty when the fetch FAILED. Distinguishing this from "no children" is
   * the whole point: the previous version swallowed every error into an empty
   * array, so an RLS denial, an expired session or a dropped connection all
   * rendered as the calm, confident message "You haven't linked any children
   * yet" — telling a parent with three linked children that they have none.
   */
  error: string;
  refresh: () => void;
}

export function useLinkedChildren(accountId?: string): LinkedChildrenState {
  const live = shouldUseSupabase();
  const [linked, setLinked] = useState<LinkedChild[]>(() =>
    live ? [] : getLinkedChildren(FIXTURE_ACCOUNT_ID),
  );
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!live) {
      setLinked(getLinkedChildren(FIXTURE_ACCOUNT_ID));
      return;
    }
    if (!accountId) {
      setLinked([]);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    fetchLinkedChildren(accountId)
      .then((result) => {
        if (!active) return;
        setLinked(result);
        setError("");
      })
      .catch((err: unknown) => {
        if (!active) return;
        setLinked([]);
        setError(
          err instanceof Error && err.message
            ? err.message
            : "We couldn't load your children.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [live, accountId, tick]);

  return { linked, loading, error, refresh };
}
